// src/pages/dashboard/components/InventoryAlerts.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, PackageX, ChevronRight } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { getInventoryAlerts } from '../../../services/api/products';
import { getUserWarehouses } from '../../../services/api/packingBatch';
import styles from './InventoryAlerts.module.css';

const InventoryAlerts = () => {
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [threshold, setThreshold] = useState(10);

  useEffect(() => {
    if (hasPermission('view_all_products')) {
      fetchWarehouses();
    }
  }, []);

  useEffect(() => {
    if (hasPermission('view_all_products')) {
      fetchAlerts();
    }
  }, [selectedWarehouse, threshold]);

  const fetchWarehouses = async () => {
    try {
      const response = await getUserWarehouses();
      setWarehouses(response.warehouses || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { lowStockThreshold: threshold };
      if (selectedWarehouse) {
        params.warehouseId = selectedWarehouse;
      }
      const response = await getInventoryAlerts(params);
      setData(response);
    } catch (err) {
      console.error('Error fetching inventory alerts:', err);
      setError(err.message || t('dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  if (!hasPermission('view_all_products')) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.inventoryAlerts')}</h3>
        </div>
        <div className={styles.skeleton}>
          <div className={styles.skeletonBar}></div>
          <div className={styles.skeletonBar}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.inventoryAlerts')}</h3>
        </div>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={fetchAlerts} className={styles.retryButton}>
            {t('dashboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { summary, lowStockItems = [], outOfStockItems = [] } = data;
  const hasAlerts = summary.lowStockCount > 0 || summary.outOfStockCount > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>{t('dashboard.inventoryAlerts')}</h3>
        <Link to="/products" className={styles.viewAllLink}>
          {t('dashboard.viewAll')}
          <ChevronRight size={16} />
        </Link>
      </div>

      <div className={styles.filters}>
        {warehouses.length > 1 && (
          <select
            className={styles.warehouseSelect}
            value={selectedWarehouse || ''}
            onChange={(e) => setSelectedWarehouse(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">{t('dashboard.allWarehouses')}</option>
            {warehouses.map(warehouse => (
              <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
            ))}
          </select>
        )}

        <select
          className={styles.thresholdSelect}
          value={threshold}
          onChange={(e) => setThreshold(parseInt(e.target.value))}
        >
          <option value="5">{t('dashboard.threshold')}: 5</option>
          <option value="10">{t('dashboard.threshold')}: 10</option>
          <option value="20">{t('dashboard.threshold')}: 20</option>
          <option value="50">{t('dashboard.threshold')}: 50</option>
        </select>
      </div>

      <div className={styles.summary}>
        <div className={styles.alertCard}>
          <div className={`${styles.alertIcon} ${styles.warning}`}>
            <AlertTriangle size={20} />
          </div>
          <div className={styles.alertInfo}>
            <div className={styles.alertValue}>{formatNumber(summary.lowStockCount)}</div>
            <div className={styles.alertLabel}>{t('dashboard.lowStockItems')}</div>
          </div>
        </div>

        <div className={styles.alertCard}>
          <div className={`${styles.alertIcon} ${styles.danger}`}>
            <PackageX size={20} />
          </div>
          <div className={styles.alertInfo}>
            <div className={styles.alertValue}>{formatNumber(summary.outOfStockCount)}</div>
            <div className={styles.alertLabel}>{t('dashboard.outOfStockItems')}</div>
          </div>
        </div>
      </div>

      {hasAlerts ? (
        <div className={styles.content}>
          {outOfStockItems.length > 0 && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>{t('dashboard.outOfStock')}</h4>
              <div className={styles.itemList}>
                {outOfStockItems.slice(0, 5).map((item, index) => (
                  <div key={index} className={styles.item}>
                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>{item.productName}</div>
                      <div className={styles.itemSku}>{item.sku}</div>
                    </div>
                    <div className={`${styles.itemStock} ${styles.outOfStock}`}>
                      {formatNumber(item.stockQuantity)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {lowStockItems.length > 0 && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>{t('dashboard.lowStock')}</h4>
              <div className={styles.itemList}>
                {lowStockItems.slice(0, 5).map((item, index) => (
                  <div key={index} className={styles.item}>
                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>{item.productName}</div>
                      <div className={styles.itemSku}>{item.sku}</div>
                    </div>
                    <div className={`${styles.itemStock} ${styles.lowStock}`}>
                      {formatNumber(item.stockQuantity)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.emptyState}>
          <p>{t('dashboard.noInventoryAlerts')}</p>
        </div>
      )}
    </div>
  );
};

export default InventoryAlerts;